import joi from 'joi';
import {
  AccessImpactMode,
  ActionCondition,
  ActionTypes,
  EcommercePurchaseFactorField,
  ImpactMode,
  PeriodicityLimitationEnum,
  QualifioCampaignFactorField,
  QualifioLoyaltyBirthdayFactorField,
  QualifioLoyaltyMemberVisitFactorField,
  RuleAttributes,
  RuleI18nAttributes,
  RuleInput,
  RuleType,
} from './types/rule.type';
import { EventName, EventSource } from '../api-client/events';

export enum fixedEarningEventNames {
  PROFILE_CREATION = EventName.PROFILE_CREATION,
  LEVEL_CHANGE = EventName.LEVEL_CHANGE,
  BASIC = EventName.BASIC,
  MEMBER_CLICK = EventName.MEMBER_CLICK,
  PREDICTION = EventName.PREDICTION,
}

const earningFactors: { [key: string]: string[] } = {
  'QUALIFIO_ENGAGE-CAMPAIGN': [
    QualifioCampaignFactorField.DISTANCE,
    QualifioCampaignFactorField.POINT,
    QualifioCampaignFactorField.TIME,
  ],
  'QUALIFIO_LOYALTY-MEMBER_VISIT': [
    QualifioLoyaltyMemberVisitFactorField.TOTAL_DAILY_VISITS,
    QualifioLoyaltyMemberVisitFactorField.CONSECUTIVE_DAILY_VISITS,
  ],
  'QUALIFIO_LOYALTY-BIRTHDAY': [QualifioLoyaltyBirthdayFactorField.AGE],
  'QUALIFIO_LOYALTY-PROFILE_CREATION': [],
  'QUALIFIO_LOYALTY-LEVEL_CHANGE': [],
  'ECOMMERCE-PURCHASE': [
    EcommercePurchaseFactorField.AMOUNT_VAT_EXCLUDED,
    EcommercePurchaseFactorField.AMOUNT_VAT_INCLUDED,
    EcommercePurchaseFactorField.SHIPPING_COST_VAT_INCLUDED,
    EcommercePurchaseFactorField.TOTAL_AMOUNT_VAT_INCLUDED,
    EcommercePurchaseFactorField.BASKET_TOTAL,
    EcommercePurchaseFactorField.QUANTITY_TOTAL,
  ],
  'REFERRAL-ECOMMERCE': [
    EcommercePurchaseFactorField.AMOUNT_VAT_EXCLUDED,
    EcommercePurchaseFactorField.AMOUNT_VAT_INCLUDED,
    EcommercePurchaseFactorField.SHIPPING_COST_VAT_INCLUDED,
    EcommercePurchaseFactorField.TOTAL_AMOUNT_VAT_INCLUDED,
    EcommercePurchaseFactorField.BASKET_TOTAL,
    EcommercePurchaseFactorField.QUANTITY_TOTAL,
  ],
};

export const $ruleI18nAttributesRedirectUrlSchema = joi.when(joi.ref('....criteria'), {
  is: joi
    .object({ and: joi.array().length(0) })
    .optional()
    .allow(null),
  then: joi.string().empty(joi.any()).optional().default(''),
  otherwise: joi
    .string()
    .uri()
    .empty(joi.string().regex(/<p>(<br>|\s)*<\/p>/i)),
});

export const ruleI18nAttributesSchema = joi.object<RuleI18nAttributes>().pattern(
  /[a-z]+/i,
  joi.object({
    description: joi
      .string()
      .required()
      .empty(joi.string().regex(/^<p>(<br>|\s)*<\/p>$/i)),
    redirectUrl: joi.when(joi.ref('....type'), {
      is: joi.string().valid(RuleType.ACCESS),
      then: $ruleI18nAttributesRedirectUrlSchema,
      otherwise: joi
        .string()
        .optional()
        .empty(
          joi
            .string()
            .regex(/^<p>(<br>|\s)*<\/p>$/i)
            .allow('')
            .strip(),
        ),
    }),
  }),
);

export const earningActionSchema = (eventSource: string, eventName: string) =>
  joi.object({
    kind: joi
      .string()
      .valid(...Object.values(ActionTypes))
      .required(),
    impact: joi
      .object({
        mode: joi.string().valid(...Object.values(ImpactMode)),
        value: joi
          .when('mode', {
            is: ImpactMode.FIXED_AMOUNT,
            then: joi.number().min(0).max(99999999999).required(),
            otherwise: joi.any().strip(),
          })
          .optional(),
        factor: joi
          .when('mode', {
            is: ImpactMode.VARIABLE_AMOUNT,
            then: joi.number().min(0).max(99999999999).precision(4).required(),
            otherwise: joi.any().strip(),
          })
          .optional(),
        factorField: joi
          .when('mode', {
            is: ImpactMode.VARIABLE_AMOUNT,
            then: joi
              .string()
              .required()
              .custom((factorField: string, helper) => {
                if (eventSource === EventSource.BINARY) {
                  return factorField;
                }
                const { error, value } = joi
                  .string()
                  .valid(...earningFactors[`${eventSource}-${eventName}`])
                  .validate(factorField);
                if (error) {
                  return helper.error('custom.earning.factorfield.error"');
                } else {
                  return value;
                }
              }),
            otherwise: joi.any().strip(),
          })
          .optional(),
        currency: joi.string().uuid().required(),
      })
      .optional(),
  });

export const burningActionSchema = joi.object({
  kind: joi.string().valid(ActionTypes.TRANSACTION, ActionTypes.REWARD_ATTRIBUTION).required(),
  impact: joi
    .when('kind', {
      is: ActionTypes.TRANSACTION,
      then: joi
        .object({
          mode: joi.string().valid(ImpactMode.FIXED_AMOUNT).required(),
          currency: joi.string().uuid().required(),
          value: joi.number().min(-99999999999).max(99999999999).required(),
        })
        .required(),
      otherwise: joi.object({
        rewardId: joi.string().required(),
      }),
    })
    .required(),
});

export const accessActionSchema = joi
  .object({
    kind: joi.string().valid(ActionTypes.ACCESS).required(),
    actionCondition: joi
      .string()
      .valid(...Object.values(ActionCondition))
      .required(),
    impact: joi
      .object({
        mode: joi
          .string()
          .valid(...Object.values(AccessImpactMode))
          .required(),
      })
      .required(),
  })
  .required();

export const $criteria = joi
  .object()
  .optional()
  .allow(null)
  .empty('')
  .custom((value, helpers) => {
    const str = JSON.stringify(value);
    if (str.length > 5000) {
      return helpers.error('any.ruleTooComplex');
    }
    return value;
  });

export const ruleActionsSchema = joi
  .when('type', { is: RuleType.BURN, then: joi.array().items(burningActionSchema) })
  .when('type', { is: RuleType.ACCESS, then: joi.array().items(accessActionSchema) })
  .when('type', {
    is: RuleType.EARN,
    then: joi
      .when('eventSource', {
        is: EventSource.ECOMMERCE,
        then: joi.when('eventName', {
          is: EventName.PURCHASE,
          then: joi
            .array()
            .items(earningActionSchema(EventSource.ECOMMERCE, EventName.PURCHASE))
            .required(),
        }),
      })
      .when('eventSource', {
        is: EventSource.QUALIFIO_ENGAGE,
        then: joi.when('eventName', {
          is: EventName.CAMPAIGN,
          then: joi
            .array()
            .items(earningActionSchema(EventSource.QUALIFIO_ENGAGE, EventName.CAMPAIGN))
            .required(),
        }),
      })
      .when('eventSource', {
        is: EventSource.ADVANCED_FORMAT,
        then: joi.when('eventName', {
          is: EventName.PREDICTION,
          then: joi
            .array()
            .items(earningActionSchema(EventSource.ADVANCED_FORMAT, EventName.PREDICTION))
            .required(),
        }),
      })
      .when('eventSource', {
        is: EventSource.QUALIFIO_LOYALTY,
        then: joi
          .when('eventName', {
            is: EventName.MEMBER_VISIT,
            then: joi
              .array()
              .items(earningActionSchema(EventSource.QUALIFIO_LOYALTY, EventName.MEMBER_VISIT))
              .required(),
          })
          .when('eventName', {
            is: EventName.BIRTHDAY,
            then: joi
              .array()
              .items(earningActionSchema(EventSource.QUALIFIO_LOYALTY, EventName.BIRTHDAY))
              .required(),
          })
          .when('eventName', {
            is: EventName.PROFILE_CREATION,
            then: joi
              .array()
              .items(earningActionSchema(EventSource.QUALIFIO_LOYALTY, EventName.PROFILE_CREATION))
              .required(),
          })
          .when('eventName', {
            is: EventName.LEVEL_CHANGE,
            then: joi
              .array()
              .items(earningActionSchema(EventSource.QUALIFIO_LOYALTY, EventName.LEVEL_CHANGE))
              .required(),
          })
          .when('eventName', {
            is: EventName.MEMBER_CLICK,
            then: joi
              .array()
              .items(earningActionSchema(EventSource.QUALIFIO_LOYALTY, EventName.MEMBER_CLICK))
              .required(),
          }),
      })
      .when('eventSource', {
        is: EventSource.BINARY,
        then: joi
          .array()
          .items(earningActionSchema(EventSource.BINARY, EventName.BASIC))
          .required(),
      })
      .when('eventSource', {
        is: EventSource.REFERRAL,
        then: joi.when('eventName', {
          is: EventName.ECOMMERCE,
          then: joi
            .array()
            .items(earningActionSchema(EventSource.REFERRAL, EventName.ECOMMERCE))
            .required(),
        }),
      }),
  });

export const $ruleName = joi.string().required();

export const periodicityLimitationSchema = joi
  .object({
    enabled: joi.boolean().required(),
    periodicity: joi
      .string()
      .valid(...Object.values(PeriodicityLimitationEnum))
      .required(),
    occurrence: joi.number().optional().default(1),
    timezone: joi.when('.periodicityLimitation', {
      is: true,
      then: joi.string().required().empty(''),
      otherwise: joi.string().optional().empty(''),
    }),
  })
  .optional();
export const ruleSchema = joi.object<RuleInput>({
  name: $ruleName,
  eventSource: joi
    .string()
    .valid(...Object.values(EventSource))
    .required(),
  eventName: joi.when('eventSource', {
    is: EventSource.BINARY,
    then: joi.string().required(),
    otherwise: joi.string().valid(...Object.values(EventName)),
  }),
  type: joi
    .string()
    .valid(...Object.values(RuleType))
    .required(),
  i18nAttributes: ruleI18nAttributesSchema,
  criteria: $criteria,
  actions: ruleActionsSchema,
  options: joi.object({ periodicityLimitation: periodicityLimitationSchema }).required(),
  version: joi.number().required(),
  positionVersion: joi.number().required(),
  valid: joi.object().optional().allow(null).empty(''),
  deletedAt: joi.string().isoDate().optional().allow(null).empty(''),
});

/** Request payload for programmatic replace-import (validated by {@link replaceRulesBodySchema}). */
export type ReplaceRulesBody = {
  oldRules: Pick<RuleAttributes, 'id' | 'version' | 'positionVersion'>[];
  newRules: RuleInput[];
};

export const replaceRulesRuleInputSchema = ruleSchema
  .keys({
    version: joi.number().optional().strip(),
    positionVersion: joi.number().optional().strip(),
    position: joi.any().optional().strip(),
    valid: joi.any().optional().strip(),
    deletedAt: joi.any().optional().strip(),
  })
  .append({
    id: joi.any().optional().strip(),
    programId: joi.any().optional().strip(),
    accountId: joi.any().optional().strip(),
    createdAt: joi.any().optional().strip(),
    updatedAt: joi.any().optional().strip(),
  })
  .unknown(false);

export const oldRulesOptimisticCheckSchema = joi.object({
  id: joi.string().uuid().required(),
  version: joi.number().required(),
  positionVersion: joi.number().required(),
});

export const replaceRulesBodySchema = joi.object<ReplaceRulesBody>({
  oldRules: joi.array().items(oldRulesOptimisticCheckSchema).required(),
  newRules: joi.array().items(replaceRulesRuleInputSchema).required(),
});
